import type { ObjectContext } from 'Shared/posters/project/objects';
import { setObjectPositionSize, setDetachedInStore } from 'Shared/posters/project/objects';
import type { DesignLinkContext } from 'Shared/posters/project/designLink';
import type { HistoryContext } from 'Shared/posters/project/history';
import type { Mode } from 'Shared/posters/project/PosterMode';
import type { DraggableImpl, IObjectImpl, Dimensions } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { Box, Command, Position } from 'Shared/posters/types';

// What a gesture start detached: the origin to work from (the leaf's laid-out position, so it doesn't
// jump) and the template slot it vacated, carried through to the commit so undo can relink it.
export interface DetachedStart
{
	pos:  Position;
	slot: Dimensions;
}

// A linked leaf (user mode, not detached) takes its geometry from the template's laid-out box, so it
// reflows with the record; a detached or unlinked object uses its own live box. Shared by the host
// <g>, the selection overlay, the intercept rect and the resize handles so all of them track the same
// box. Reads size only off the design side (laidOut is position+size, both cycle-free), never a live
// layoutPos.
//
// displayBox turns what the layout *placed* into what the object *draws*: a field is laid out in its
// design width (so its container aligns the reserved box) and then shrink-wraps back onto its ink, so
// selecting it in user mode hugs the text. Identity for everything else.
export function linkedBox(mode: Mode, design: DesignLinkContext, impl: DraggableImpl, userExists: boolean): Box
{
	if (mode == 'user' && design.linked()) {
		const laid = design.laidOut(impl.id);
		if (laid) return impl.displayBox(laid);
	}
	return impl.effectiveBox(userExists);
}

// lookupLeaf — the user-mode size seam, the same conversion as linkedBox read for its dims: a linked
// (non-detached) leaf presents the size the template laid it out at, shrink-wrapped, so it matches its
// reflowed position; a detached or unlinked object measures its own live box.
export function lookupLeaf(mode: Mode, design: DesignLinkContext, impl: DraggableImpl): Dimensions
{
	if (mode == 'user' && design.linked()) {
		const laid = design.laidOut(impl.id);
		if (laid) return impl.displayBox(laid).dims;
	}
	return impl.getSize();
}

// A linked leaf detaches when it begins a free drag or a resize: it stops following the template layout
// and owns its own geometry, while a fixed-size dummy holds its slot in the template so the other leaves
// stay put. The snapshot is its current laid-out box (so it doesn't jump); that position is written to
// the flat store and the live box as the new free origin. Returns the origin to work from, or null if it
// wasn't a linked leaf. Detaching flips design.laidOut(id) to undefined, so the host <g>, lookupLeaf and
// the resize handles fall back to this same live box for the rest of the gesture.
//
// Until the leaf is detached the template keeps placing it, so a *position* write is simply ignored: a
// drag would not move it, and a resize from a north/west handle would leave the origin pinned and grow
// the box from the opposite corner. Only its size feeds back (through the layout's metricsOf), which is
// why an undetached resize looked like it worked from the wrong corner.
//
// The detachment is written into the design (the leaf's own `detached` slot) rather than held in a
// runtime signal, so it is saved with the geometry the gesture produces. Without it the leaf reloads
// linked, is laid out at the template's position again, and the settle bake writes that position back
// over the dragged one — the gesture survived neither the reload nor the DB.
//
// The two conversions run in the same order as the bake (see bakeLinkedPositions): the laid-out box is
// what the layout *placed*, displayBox() turns it into the box the leaf draws — which the live box takes
// verbatim, so nothing jumps — and storedPositionOf() turns that into the design position the store
// holds, the same conversion a drag commit makes. Writing the live position into the store instead
// re-entered adoptDesignGeometry, which re-anchored it through livePosition and shifted the leaf by its
// hug inset the moment it was first clicked. The dummy keeps the *placed* box, so the slot is unchanged.
export function detachIfLinked(impl:IObjectImpl,design:DesignLinkContext,objCtx:ObjectContext,mode:Mode): DetachedStart | null
{
	if (mode != 'user' || !design.linked() || design.isDetached(impl.id))
		return null;
	const laid = design.laidOut(impl.id);
	if (!laid)
		return null;
	const pos = impl.displayBox(laid).pos;
	const stored = impl.storedPositionOf(pos);
	const slot = { width: laid.dims.width, height: laid.dims.height };
	setDetachedInStore(objCtx, impl.id, slot);
	impl.writePos(pos);
	setObjectPositionSize(objCtx, impl.id, stored, undefined);
	return { pos, slot };
}

// A detachment taken by a gesture, waiting for that gesture's commit to carry it into history.
export interface DetachmentCommit
{
	// Hand this to the code that commits the gesture in place of the real history context.
	history:   HistoryContext;
	// True once a command has gone through it — i.e. some history entry now carries the relink.
	committed: () => boolean;
}

// Folds a detachment into whatever command a gesture commits, so the geometry and the relink undo as
// one step. A resize reaches history through a different command for each object type (resizeObjectCmd,
// cornerResizeCmd, fieldReserveCmd, setSoftWrapsCmd — the last three chosen inside the impl, several
// calls below the gesture that detached), so the slot is carried on the *context* rather than threaded
// as a parameter through every one of them.
//
// The commit is not guaranteed: an impl whose resize end is inert executes nothing, and the caller then
// has to give the detachment back — hence committed(). Without that a leaf could be left off the
// template layout with no history entry able to put it back.
export function detachmentCommit(histCtx: HistoryContext, objCtx: ObjectContext, id: string, slot: Dimensions): DetachmentCommit
{
	let done = false;
	return {
		history: {
			...histCtx,
			execute: (cmd: Command) => { done = true; histCtx.execute(relinkOnUndo(cmd, objCtx, id, slot)) },
		},
		committed: () => done,
	}
}

// mergeKey is carried through untouched: history merges a later command into this one by keeping *this*
// undo (see createHistoryState.execute), so a run of merged resize frames still relinks when undone.
function relinkOnUndo(cmd: Command, objCtx: ObjectContext, id: string, slot: Dimensions): Command
{
	return {
		...cmd,
		execute() { cmd.execute(); setDetachedInStore(objCtx, id, slot) },
		undo()    { cmd.undo();    setDetachedInStore(objCtx, id, null) },
	}
}
